import React from "react";
import Panel from "../../components/ui/Panel/Panel.jsx";
import PageHeader from "../../components/marketing/PageHeader.jsx";
import SeoHead from "../../seo/SeoHead.jsx";
import { ROUTE_META } from "../../seo/siteMeta.js";

// Ported from form720-ui/src/Pages/legal/PrivacyPolicyPage.jsx and adapted for
// Form 2290. Same company (Quantiva LLC), same mailing address, same substance;
// what changed is the product name, the form and its title, the support address,
// and the description of what we collect (Form 2290 needs vehicle identification
// numbers and weight categories, which Form 720 does not).
//
// NOTE FOR THE OWNER — carried over from the 720 version and still true here:
// - US-general consumer privacy policy. Section 12 gives a plain-language,
//   multi-state rights notice (covers CCPA/CPRA-style rights generically). It
//   does NOT include EU/UK (GDPR) rights — add those if you market to the EU/UK.
// - If CCPA/CPRA applies to you (revenue/volume thresholds), counsel may want a
//   more detailed California-specific notice than the generic Section 12.
// - Have counsel review before relying on this as binding.
const SECTIONS = [
  {
    title: "1. Introduction",
    body:
      'This Privacy Policy explains how Quantiva LLC ("Quantiva," "we," "us," or "our") collects, uses, ' +
      'shares, and protects information in connection with EasyFile2290 (the "Service"), our online platform ' +
      "for preparing and electronically filing IRS Form 2290 (Heavy Highway Vehicle Use Tax Return). By creating " +
      "an account or using the Service, you agree to the practices described here.",
  },
  {
    title: "2. Information you provide to us",
    body:
      "We collect the information you enter into the Service so we can prepare and file your return: your account " +
      "details (such as your name and email address), the business information needed to complete your Form 2290 " +
      "(including your employer identification number), and information about each vehicle you report — its " +
      'vehicle identification number, taxable gross weight category, and how it is used. When you pay for the ' +
      'Service, your payment is handled by our payment processor (see "Payment information" below). We ask only ' +
      "for what is needed to prepare, file, and support your filing. If you rate a filing or send us feedback, we " +
      "keep your rating and comments. If you give us permission to quote a comment, we also keep the name, role, " +
      "location and typed signature you enter, and may publish the quote with them as you agreed; you can " +
      "withdraw that permission at any time in Settings. If you create your account through someone's referral " +
      "link, we record that so we can credit them. We never tell the person who referred you who you are or what " +
      "you filed - they see only how many credits they have earned.",
  },
  {
    title: "3. Information we collect automatically",
    body:
      "When you use the Service, we may automatically collect technical information, such as your IP address, " +
      "device and browser type, and how you interact with the Service. This helps us keep the Service secure and " +
      "running properly and, where analytics are enabled, understand how it is used.",
  },
  {
    title: "4. How we use your information",
    body:
      "We use your information to: prepare and transmit your IRS Form 2290 e-file submissions and return your " +
      "stamped Schedule 1; create and maintain your account; process payments for the Service; communicate with " +
      "you about your filings, deadlines, support requests, and product updates; detect and prevent fraud and " +
      "abuse; comply with legal and tax-recordkeeping obligations; and operate, secure, and improve the Service.",
  },
  {
    title: "5. How we share your information",
    body:
      "We share your information only as needed to run the Service: with the IRS, to transmit your return as you " +
      "direct; with Stripe, our payment processor, to handle your payment; and with the service providers that " +
      "help us host, secure, and support the Service under confidentiality obligations. We may also disclose " +
      "information when required by law or to protect our rights and users, or in connection with a business " +
      "transfer such as a merger or acquisition. We do not share your information for others’ independent marketing.",
  },
  {
    title: "6. We do not sell your information",
    body:
      "We do not sell or rent your personal information, and we do not share it with third parties for their own " +
      "independent marketing purposes.",
  },
  {
    title: "7. Payment information",
    body:
      "Payments are processed by Stripe, our third-party payment processor. Your card information goes directly " +
      "to Stripe, which handles it under the Payment Card Industry Data Security Standard (PCI DSS) — we never " +
      "collect or store your card details ourselves. We rely on Stripe to charge your payment, issue any refunds " +
      "you are owed, and screen transactions to guard against fraud and unauthorized use.",
  },
  {
    title: "8. Cookies and analytics",
    body:
      "We use cookies and similar technologies to operate the Service and keep you signed in. Where analytics are " +
      "enabled, we also use analytics tools (such as Google Analytics) to understand how the Service is used. You " +
      "can manage these through your browser settings.",
  },
  {
    title: "9. Data retention",
    body:
      "We retain your account, business, vehicle, and filing information for as long as your account is active and " +
      "as needed to provide the Service and to comply with our own legal, tax, and accounting obligations. Keeping " +
      "copies of your filed returns, stamped Schedule 1, and supporting documents to meet IRS recordkeeping " +
      "requirements is your responsibility as the taxpayer.",
  },
  {
    title: "10. Keeping your information safe",
    body:
      "Protecting your information is a priority for us. We use industry-standard safeguards, including encryption " +
      "and access controls, to keep your data secure, and we keep those protections up to date. No online service " +
      "can promise perfect security, so we also encourage you to keep your account credentials confidential.",
  },
  {
    title: "11. Your choices and rights",
    body:
      "You may review and update your account and business information by signing in to the Service. You may " +
      "request access to, correction of, or deletion of your personal information by contacting us at the address " +
      "below; note that we may need to retain certain information to comply with our legal obligations or to " +
      "resolve disputes. If we send you marketing emails, you can ask us to stop at any time by contacting us at " +
      "the address below; either way, we will still send transactional messages about your filings and account.",
  },
  {
    title: "12. Your U.S. state privacy rights",
    body:
      "Depending on the state you live in, you may have additional rights over your personal information — such as " +
      "the right to know what we collect, to request a copy, to correct or delete it, and not to be treated " +
      "differently for exercising these rights. We do not sell your personal information, and we do not share it " +
      "for cross-context behavioral advertising. To make a request, contact us using the details below; we will " +
      "verify it and respond as required by your state’s law. Where permitted, you may also use an authorized " +
      "agent to make a request on your behalf.",
  },
  {
    title: "13. Children’s privacy",
    body:
      "The Service is intended for businesses and individuals aged 18 and older. We do not knowingly collect " +
      "personal information from children. If you believe a child has provided us personal information, please " +
      "contact us and we will delete it.",
  },
  {
    title: "14. Third-party links",
    body:
      "The Service may link to third-party websites or resources, including the IRS. We are not responsible for " +
      "the privacy practices of those third parties, and this Policy does not apply to them. Review their privacy " +
      "policies before providing information.",
  },
  {
    title: "15. Where your information is processed",
    body:
      "We operate the Service and store data on infrastructure located in the United States. If you access the " +
      "Service from outside the United States, you understand that your information will be processed in the " +
      "United States.",
  },
  {
    title: "16. Changes to this policy",
    body:
      'We may update this Policy from time to time. The "Last updated" date above reflects the most recent ' +
      "revision. Material changes will be communicated via email or in-app notification. Your continued use of the " +
      "Service after an update means you accept the revised Policy.",
  },
  {
    title: "17. Contact us",
    body:
      "Questions or requests regarding this Privacy Policy can be sent to support@easyfile2290.com, or by mail to: " +
      "Quantiva LLC, 2591 Dallas Parkway, Suite 300 PMB 231, Frisco, TX 75034. " +
      "EasyFile2290 is a product of Quantiva LLC.",
  },
];

const meta = ROUTE_META["/privacy"];

const Privacy = () => (
  <div className="max-w-[820px] mx-auto px-[22px] py-[46px] pb-[70px]">
    <SeoHead title={meta.title} description={meta.description} path="/privacy" keywords={meta.keywords} />

    <PageHeader eyebrow="Legal" title="Privacy Policy">
      Last updated: October 8, 2026
    </PageHeader>

    <Panel padded className="mt-8 !p-[30px] text-[15px] leading-[1.7] text-[var(--ink)]">
      {SECTIONS.map((s) => (
        <section key={s.title} className="mb-7 last:mb-0">
          <h2 className="font-disp font-bold text-[17px] tracking-[-.01em] mb-2">{s.title}</h2>
          <p className="text-[var(--muted)]">{s.body}</p>
        </section>
      ))}
    </Panel>
  </div>
);

export default Privacy;
